import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/Iconify';
import InfoAlert from 'src/components/InfoAlert';
import TableActionButton from 'src/components/TableActionButton';
import { useCreateBasePolicy } from 'src/hooks/environmentWdacPolicy';
import { pagesText } from 'src/constants/pagesText';
import AddManagedBasePolicyForm from './AddManagedBasePolicyForm';
import EditManagedBasePolicyModal from './EditManagedBasePolicyModal';
import UseOtherBasePolicyForm from './UseOtherBasePolicyForm';

const ADD_VIEW = 'add';
const USE_OTHER_VIEW = 'useOther';

const DIALOG_PAPER_PROPS = { sx: { backgroundColor: 'background.dialog' } };

// Both ways of putting a base policy under portal management belong to the managed list, so the
// buttons, their dialogs and the mutations travel together. Each is two steps.
const ManagedBasePolicyActions = () => {
  const [view, setView] = useState(null);
  const [settingsFor, setSettingsFor] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: createBasePolicy, isLoading: adding } = useCreateBasePolicy();

  const closeDialog = () => setView(null);

  const openSettings = basePolicy => {
    setView(null);
    setSettingsFor(basePolicy);
  };

  const handleAdd = async values => {
    try {
      const created = await createBasePolicy(values);
      enqueueSnackbar('Managed base policy added.', { variant: 'success', autoHideDuration: 3000 });
      openSettings(created);
    } catch (error) {
      enqueueSnackbar(
        String(error).toLowerCase().includes('already exist')
          ? 'A policy with this ID already exists in this environment.'
          : pagesText.somethingWentWrong,
        { variant: 'error', autoHideDuration: 4000 },
      );
    }
  };

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      <TableActionButton
        variant="outlined"
        onClick={() => setView(ADD_VIEW)}
        startIcon={<Iconify icon="mdi:plus" width={16} height={16} />}
        sx={{ px: 1.5 }}>
        Add Managed Policy
      </TableActionButton>
      <TableActionButton
        variant="outlined"
        onClick={() => setView(USE_OTHER_VIEW)}
        startIcon={<Iconify icon="mdi:shield-search" width={16} height={16} />}
        sx={{ px: 1.5 }}>
        Use Other Base Policy
      </TableActionButton>

      <Dialog
        open={view === ADD_VIEW}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={DIALOG_PAPER_PROPS}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Iconify icon="mdi:shield-plus-outline" width={22} height={22} color="#87C8E0" />
          Add Managed Base Policy
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography variant="body2" sx={{ color: '#ccc' }}>
              Manually register a base policy for this environment so supplemental policies and
              security catalogs can be generated against it. This creates the policy and its first
              version.
            </Typography>
            <InfoAlert sx={{ py: 0.25 }}>
              <Typography variant="caption">
                Registering a base policy here tells us it exists in your organization so components
                can be generated against it. It does not give us a way to deploy the base policy
                itself, which stays with whatever already distributes it.
              </Typography>
            </InfoAlert>
            <AddManagedBasePolicyForm
              loading={adding}
              onCancel={closeDialog}
              onSubmit={handleAdd}
            />
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={view === USE_OTHER_VIEW}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={DIALOG_PAPER_PROPS}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Iconify icon="mdi:shield-search" width={22} height={22} color="#87C8E0" />
          Use Other Base Policy
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography variant="body2" sx={{ color: '#ccc' }}>
              Bring a base policy your devices already report under portal management, rather than
              registering a new one.
            </Typography>
            <UseOtherBasePolicyForm onCancel={closeDialog} onSubmit={openSettings} />
          </Stack>
        </DialogContent>
      </Dialog>

      {settingsFor ? (
        <EditManagedBasePolicyModal basePolicy={settingsFor} onClose={() => setSettingsFor(null)} />
      ) : null}
    </Stack>
  );
};

export default ManagedBasePolicyActions;
